import { useEffect, useState, useCallback } from "react";
import { Eye, EyeOff, ImagePlus, X } from "lucide-react";
import { api, photoUrl, jobPhotoSrc, formatApiError } from "../../lib/api";

export function HeroManager({ jobs }) {
  const [slides, setSlides] = useState([]);
  const [picker, setPicker] = useState(null); // slide id

  const refresh = useCallback(() => {
    api.get("/hero/all").then((r) => setSlides(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const toggle = async (s) => {
    try { await api.patch(`/hero/${s.id}`, { enabled: !s.enabled }); refresh(); }
    catch (e) { alert(formatApiError(e)); }
  };

  const setImage = async (slideId, url) => {
    try { await api.patch(`/hero/${slideId}`, { image_url: url }); setPicker(null); refresh(); }
    catch (e) { alert(formatApiError(e)); }
  };

  return (
    <section className="bg-white border-2 border-[#2C3539]/10 p-6 md:p-10 mb-6" data-testid="hero-manager">
      <div className="font-display text-3xl uppercase text-[#2C3539]">Homepage Hero</div>
      <p className="text-sm text-[#2C3539]/70 mt-1">
        Each heading rotates on the homepage with its own photo. Toggle a section on/off and change its photo here.
      </p>

      <div className="mt-5 space-y-2" data-testid="hero-slides-list">
        {slides.map((s) => (
          <div key={s.id} className={`flex flex-wrap items-center gap-3 sm:gap-4 border-2 p-3 transition-colors ${s.enabled ? "border-[#2C3539]/15" : "border-dashed border-[#2C3539]/15 opacity-60"}`} data-testid={`hero-slide-${s.id}`}>
            <img src={s.image_url} alt={s.heading} className="w-16 h-12 sm:w-20 sm:h-14 object-cover shrink-0 bg-[#2C3539]" />
            <div className="flex-1 min-w-[140px]">
              <div className="font-display text-base sm:text-lg uppercase text-[#2C3539] leading-tight">{s.heading}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: s.enabled ? "#15803D" : "#9CA3AF" }}>
                {s.enabled ? "Showing in hero" : "Hidden"}
              </div>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button onClick={() => setPicker(picker === s.id ? null : s.id)} data-testid={`hero-change-photo-${s.id}`} className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-[0.1em] border border-[#2C3539]/25 hover:border-[#2F5F80] hover:text-[#2F5F80] px-3 py-2 transition-colors">
                <ImagePlus size={13} /> Photo
              </button>
              <button onClick={() => toggle(s)} data-testid={`hero-toggle-${s.id}`} className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-[0.1em] px-3 py-2 transition-colors ${s.enabled ? "bg-[#23282B] text-white hover:bg-[#234B66]" : "bg-[#2F5F80] text-white hover:bg-[#234B66]"}`}>
                {s.enabled ? <><EyeOff size={13} /> Hide</> : <><Eye size={13} /> Show</>}
              </button>
            </div>
          </div>
        ))}
      </div>

      {picker && (
        <div className="mt-4 border-2 border-[#2C3539]/10 bg-[#F1F3F5] p-4" data-testid="hero-photo-picker">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#2F5F80]">Choose a photo from your uploads</div>
            <button onClick={() => setPicker(null)} className="p-1 text-[#23282B]/60 hover:text-[#23282B]" data-testid="hero-picker-close"><X size={16} /></button>
          </div>
          {jobs.length === 0 ? (
            <div className="mt-3 text-sm text-[#23282B]/60">No uploaded photos yet — add some in the Upload Job tab.</div>
          ) : (
            <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
              {jobs.map((j) => (
                <button key={j.id} onClick={() => setImage(picker, jobPhotoSrc(j))} data-testid={`hero-photo-option-${j.id}`} title={j.job_site_name} className="relative aspect-square overflow-hidden border-2 border-transparent hover:border-[#2F5F80] transition-colors">
                  <img src={jobPhotoSrc(j)} alt={j.job_site_name} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
